import { Component, AfterViewInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { UtilsService } from 'src/app/services/utils.service';
import { environment } from 'src/environments/environment';
import { PermissionService } from 'src/app/services/permission.service';
import { InactivityService } from 'src/app/services/inactivity.service';
import { WebsocketService } from 'src/app/services/websocket.service';
import { Warehouse, WarehouseService } from 'src/app/services/warehouse.service';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';

declare const Menu: any;
declare const window: any;

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements AfterViewInit, OnDestroy {
    constructor(
        private route: ActivatedRoute, 
        private router: Router, 
        public utils: UtilsService, 
        private permissionService: PermissionService, 
        private inactivityService: InactivityService,
        private websocketService: WebsocketService,
        private warehouseService: WarehouseService
    ) {
        
    }

    urlFiles = environment.url_page

    dataChildren: any = {}
    dataUser: any = {}
    private menuInstance: any;
    servicesOpen:{[key:string]: boolean}= {};
    configOpen = false;
    menuOpen = false;
    warehouseOpen = false;
    // El catálogo y la selección viven en WarehouseService para que las
    // pantallas hijas lean el mismo almacén que muestra este selector.
    warehouses = this.warehouseService.warehouses;
    currentWarehouse = this.warehouseService.current;
    /** Lo que pinta el desplegable: el almacén actual siempre en primer lugar. */
    sortedWarehouses = this.warehouseService.sorted();
    userInitials = '';
    roleLabel = '';
    permissions: any = [];
    @ViewChild(RouterOutlet) outlet!: RouterOutlet;
    private inactivitySub?: Subscription;
    private warningSub?: Subscription;
    private warningOpen = false;
    private permissionsUpdatedSub?: Subscription;

    ngOnInit(): void {
        this.dataUser = this.utils.getDataUser()
        this.userInitials = this.computeInitials(this.dataUser);
        this.getPermissions();
        
        // Initialize WebSocket notifications for the current user by role
        
        // Fallback: try to get user data from localStorage if utils.getDataUser() fails
        if (!this.dataUser || !this.dataUser.id) {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                try {
                    this.dataUser = JSON.parse(storedUser);
                } catch (e) {
                    console.error('❌ Failed to parse user data from localStorage:', e);
                }
            }
        }
        
        if (this.dataUser && this.dataUser.id && this.dataUser.role_id) {
            // Convert to numbers to ensure proper type
            const userId = Number(this.dataUser.id);
            const roleId = Number(this.dataUser.role_id);
            this.websocketService.subscribeToRoleNotifications(userId, roleId);
            // Refresca el menú lateral en vivo cuando un admin cambia los
            // permisos de este rol, sin necesidad de recargar la página.
            this.permissionsUpdatedSub = this.websocketService.getPermissionsUpdates().subscribe(data => {
                if (data && Number(data.role_id) === roleId) {
                    this.getPermissions(true);
                }
            });
        } else if (this.dataUser && this.dataUser.id) {
            // Fallback to user-specific notifications if no role_id
            const userId = Number(this.dataUser.id);
            this.websocketService.subscribeToUserNotifications(userId);
        } else {
            console.error('❌ No valid user data found for WebSocket subscription');
            // Temporary test with hardcoded values
            this.websocketService.subscribeToUserNotifications(1);
        }
        // Start inactivity watcher (2 minutes)
        // 2 minutes total, warning 30 seconds before
        this.inactivityService.init(60 * 60 * 1000, 30 * 1000);
        // Subscribe to warning to prompt user to stay active
        this.warningSub = this.inactivityService.onWarning$.subscribe(() => {
            if (this.warningOpen) return;
            this.warningOpen = true;
            let countdownInterval: any;
            const totalSeconds = 30;
            Swal.fire({
                title: 'Sesión a punto de cerrar',
                html: `Tu sesión se cerrará por inactividad en <b><span id="swal-countdown">${totalSeconds}</span>s</b>. ¿Deseas permanecer activo?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Permanecer activo',
                cancelButtonText: 'Cerrar sesión ahora',
                allowOutsideClick: false,
                allowEscapeKey: true,
                didOpen: () => {
                    const el = document.getElementById('swal-countdown');
                    let remaining = totalSeconds;
                    countdownInterval = setInterval(() => {
                        remaining = Math.max(0, remaining - 1);
                        if (el) el.textContent = String(remaining);
                        if (remaining <= 0) {
                            clearInterval(countdownInterval);
                            countdownInterval = undefined;
                        }
                    }, 1000);
                },
                willClose: () => {
                    if (countdownInterval) {
                        clearInterval(countdownInterval);
                        countdownInterval = undefined;
                    }
                }
            }).then(result => {
                this.warningOpen = false;
                if (result.isConfirmed) {
                    // User wants to stay active: reset timer
                    this.inactivityService.resetTimer();
                } else {
                    // User cancelled or dismissed: logout immediately
                    this.logout();
                }
            });
        });
        this.inactivitySub = this.inactivityService.onTimeout$.subscribe(() => {
            try { Swal.close(); } catch {}
            this.logout();
        });
        // Set initial data
        this.updateChildData();

        // Update on every navigation end within this component's child routes
        this.router.events.subscribe(evt => {
            if (evt instanceof NavigationEnd) {
                this.updateChildData();
                this.menuOpen = false;
            }
        });
    }

    ngAfterViewInit(): void {
        // Initialize the vendor Menu after Angular has rendered the sidebar
        const layoutMenuEl = document.getElementById('layout-menu');
        if (layoutMenuEl && typeof Menu !== 'undefined') {
            // Avoid double initialization if global scripts already created the menu
            if (!window?.Helpers?.mainMenu) {
                this.menuInstance = new Menu(layoutMenuEl, {
                    orientation: layoutMenuEl.classList.contains('menu-horizontal') ? 'horizontal' : 'vertical',
                    closeChildren: layoutMenuEl.classList.contains('menu-horizontal') ? true : false,
                    showDropdownOnHover:
                        window?.templateCustomizer?.settings?.defaultShowDropdownOnHover ?? true
                });
                if (window?.Helpers) {
                    window.Helpers.mainMenu = this.menuInstance;
                }
            }
            if (window?.Helpers) {
                window.Helpers.scrollToActive(false);
            }
        }
    }

    logout() {
        localStorage.removeItem("token")
        localStorage.removeItem("dataUser")
        this.permissionService.reset();
        this.router.navigate(['/login']);
    }

    getPermissions(force: boolean = false) {
        this.permissionService.loadForUser(this.dataUser.id, { force }).subscribe((resp) => {
            this.permissions = this.filterInternalModulesForMenu(this.permissionService.getModules());
            // this.permissions = this.flattenControlGastos(this.permissionService.getModules());
            this.roleLabel = resp?.role?.name;
        });
    }

    /** Módulos que existen únicamente para validar permisos de páginas
     * internas — no son opciones de menú navegables (a "Recibos
     * electrónicos" solo se llega desde el detalle de un periodo de
     * nómina). Se ocultan SOLO de esta vista del sidebar: PermissionGuard y
     * canDo() siguen usando el árbol completo sin filtrar (this.permissionService.getModules()),
     * así que la ruta y sus acciones funcionan igual para quien tenga el
     * permiso, aunque no aparezca como opción de menú. El admin de permisos
     * también sigue mostrándolo, para poder otorgar/quitar el acceso. */
    private static readonly INTERNAL_ONLY_MODULES = new Set(['Recibos electrónicos']);

    private filterInternalModulesForMenu(modules: any[]): any[] {
        if (!Array.isArray(modules)) return modules;
        return modules
            .filter(m => !DashboardComponent.INTERNAL_ONLY_MODULES.has(m?.name))
            .map(m => ({
                ...m,
                children: Array.isArray(m.children) ? this.filterInternalModulesForMenu(m.children) : m.children
            }));
    }

    /**
     * Sub-secciones de Facturación. El backend sólo expone el módulo padre
     * (`modules.route = 'invoicing/list'`), así que el desglose vive aquí: ambas
     * heredan el permiso del módulo padre.
     */
    private static readonly INVOICING_CHILDREN = [
        { name: 'Facturar servicios', route: 'invoicing/list', icon: 'invoice.svg' },
        { name: 'Facturas de almacén', route: 'invoicing/warehouse', icon: 'invoice.svg' }
    ];

    private flattenControlGastos(modules: any[]): any[] {
        if (!Array.isArray(modules)) return modules;
        const result: any[] = [];
        for (const mod of modules) {
            const name = String(mod?.name || '').toLowerCase();
            if (name === 'control de gastos') {
                const invoicing = (mod?.children || []).find(
                    (c: any) => String(c?.name || '').toLowerCase().startsWith('facturaci')
                );
                if (invoicing) {
                    result.push({
                        ...invoicing,
                        icon: 'invoice.svg',
                        children: DashboardComponent.INVOICING_CHILDREN
                    });
                }
                continue;
            }
            result.push(mod);
        }
        return result;
    }

    ngOnDestroy(): void {
        // Best-effort cleanup if the library exposes a destroy method
        try {
            if (this.menuInstance && typeof this.menuInstance.destroy === 'function') {
                this.menuInstance.destroy();
            }
        } catch { }
        // stop inactivity watcher and unsubscribe
        try { this.inactivityService.stop(); } catch {}
        if (this.inactivitySub) {
            this.inactivitySub.unsubscribe();
            this.inactivitySub = undefined;
        }
        if (this.warningSub) {
            this.warningSub.unsubscribe();
            this.warningSub = undefined;
        }
        if (this.permissionsUpdatedSub) {
            this.permissionsUpdatedSub.unsubscribe();
            this.permissionsUpdatedSub = undefined;
        }
    }

    toggleMenu() {
        this.menuOpen = !this.menuOpen;
    }

    closeMenu() {
        this.menuOpen = false;
    }

    // Fallback toggle in case vendor Menu fails to bind events
    toggleServices(evt: Event, item:any) {
        evt.preventDefault();
        evt.stopPropagation();
        this.servicesOpen[item] = !this.servicesOpen[item];
    }

    // Toggle configuration menu
    toggleConfiguration(evt: Event) {
        evt.preventDefault();
        evt.stopPropagation();
        this.configOpen = !this.configOpen;
    }

    /**
     * Módulos de facturación: comparten la cabecera plana y no usan el botón
     * genérico "Actualizar" (cada pantalla trae sus propias acciones).
     */
    private static readonly INVOICING_MODULES = [
        'Facturación',
        'Facturas de almacén',
        'Facturación / Facturación de almacén'
    ];

    isInvoicingModule(): boolean {
        return DashboardComponent.INVOICING_MODULES.includes(this.dataChildren?.module);
    }

    toggleWarehouse(evt: Event) {
        evt.preventDefault();
        evt.stopPropagation();
        this.warehouseOpen = !this.warehouseOpen;
    }

    // sortedWarehouses se recalcula aquí y no desde el template: devolver el
    // array armado crearía uno nuevo en cada ciclo de detección de cambios.
    selectWarehouse(warehouse: Warehouse) {
        this.warehouseService.select(warehouse);
        this.currentWarehouse = this.warehouseService.current;
        this.sortedWarehouses = this.warehouseService.sorted();
        this.warehouseOpen = false;
    }

    // Cierra el selector de almacén al hacer clic fuera de él.
    @HostListener('document:click')
    onDocumentClick() {
        this.warehouseOpen = false;
    }

    newWarehouseInvoice() {
        this.router.navigate(['/invoicing/warehouse/new']);
    }

    // Relay refresh action to the active child component (if it exposes refreshGrid)
    refreshGrid(type:any) {
        const comp: any = (this.outlet && (this.outlet as any).component) ? (this.outlet as any).component : null;
        if (comp && typeof comp.refreshGrid === 'function' && type == 'refresh') {
            comp.refreshGrid();
        }
        if (comp && typeof comp.openModalAdd === 'function' && type == 'add') {
            comp.openModalAdd();
        }
        if (comp && typeof comp.addSupplier === 'function' && type == 'add') {
            comp.addSupplier();
        }
    }

    private computeInitials(user: any): string {
        const src = user?.full_name || user?.name || user?.username || user?.email || '';
        if (!src) return '';
        // Split by spaces or punctuation and take first letters of first two tokens
        const tokens = String(src).trim().split(/[\s._-]+/).filter(Boolean);
        const first = tokens[0]?.charAt(0) || '';
        const second = tokens.length > 1 ? tokens[1]?.charAt(0) : (user?.last_name?.charAt(0) || '');
        return (first + (second || '')).toUpperCase();
    }

    private readonly NO_BACK_TYPES = new Set(['list', 'deposits', 'roles', 'notifications']);

    private readonly MODULE_LIST_ROUTES: { [module: string]: string } = {
        'Servicio': '/service/list',
        'Control de gastos': '/service/deposits',
        'Cliente': '/client/list',
        'Conductor': '/driver/list',
        'Auxiliar': '/assistant/list',
        'Unidad': '/unit/list',
        'Remolque': '/trailer/list',
        'Mantenimiento de unidades': '/maintenance/list',
        'Carta Porte': '/carta-porte/list',
        'Facturación': '/invoicing/list',
        'Proveedor': '/provider/list',
    };

    private updateChildData(): void {
        const child = this.route?.snapshot?.firstChild;
        const childData = { ...(child?.data || {}) };
        const routePath = child?.routeConfig?.path || '';
        const folio = child?.queryParams?.['folio'];

        if ((routePath.startsWith('add') || routePath.startsWith('update') || routePath.startsWith('view')) && folio && childData['accion']) {
            childData['accion'] = `${childData['accion']} / Folio ${folio}`;
        }

        // Igual que el folio, pero para el nombre del proveedor (view/update
        // de Proveedor) — así el título del topbar no depende de que cada
        // componente arme su propio breadcrumb manual.
        const name = child?.queryParams?.['name'];
        if ((routePath.startsWith('update') || routePath.startsWith('view')) && name && childData['accion']) {
            childData['accion'] = `${childData['accion']} / ${name}`;
        }

        if (childData['type'] === 'expense-report') {
            const from = child?.queryParams?.['from'];
            const tab  = child?.queryParams?.['tab'];
            childData['backRoute']  = from === 'services' ? '/service/list' : '/service/deposits';
            childData['backParams'] = tab ? { tab } : {};
        } else if (!this.NO_BACK_TYPES.has(childData['type'])) {
            const backRoute = this.MODULE_LIST_ROUTES[childData['module']];
            if (backRoute) {
                childData['backRoute'] = backRoute;
                childData['backParams'] = {};
            }
        }

        this.dataChildren = childData;
    }

    /**
     * Rutas ya construidas, cacheadas por su string de origen.
     *
     * `getRoute` se evalúa en cada ciclo de detección de cambios. Si devuelve un
     * array nuevo cada vez, `RouterLink.ngOnChanges` se dispara en todos los
     * ciclos; y cuando un `routerLinkActive` envuelve dos o más links cuyo estado
     * activo difiere del suyo (un menú desplegable con varios hijos, donde sólo
     * uno está activo), eso realimenta `update()` -> microtarea -> tick -> ...
     * y cuelga la pestaña. Devolver siempre la misma referencia por ruta lo evita.
     */
    private readonly routeCache = new Map<string, string[]>();

    getRoute(item: any) {
        const raw: string = item?.route ?? '';
        let route = this.routeCache.get(raw);
        if (!route) {
            // Support both dot- and slash-delimited routes:
            //   "setting.permissions" -> ['/', 'setting', 'permissions']
            //   "service/list"        -> ['/', 'service', 'list']
            const segments = raw.split(/[./]/).filter(Boolean);
            route = ['/', ...segments];
            this.routeCache.set(raw, route);
        }
        return route;
    }

    private routePath(item: any): string {
        return '/' + this.getRoute(item).slice(1).join('/');
    }

    /**
     * Resalta el ítem del menú activo sin depender de la directiva RouterLinkActive:
     * con módulos cargados de forma asíncrona (permisos vía HTTP) y anidados, esa
     * directiva podía dejar el navegador colgado en la primera carga en frío de una
     * pantalla dentro de un grupo con submenú. Se calcula aquí a mano, comparando
     * contra this.router.url, que ya se re-evalúa en cada NavigationEnd suscrito
     * más abajo (ver this.router.events.subscribe en ngOnInit).
     */
    /** Primer segmento de una ruta ("/employee/13" -> "employee"), para
     * detectar rutas hermanas del mismo modulo (ej. "/employee/list" y
     * "/employee/13" son ambas del modulo "Colaboradores", aunque la segunda
     * no sea una sub-ruta textual de la primera). */
    private baseSegment(path: string): string {
        return path.split('/').filter(Boolean)[0] || '';
    }

    /** Aplana el árbol de módulos a solo los ítems realmente navegables
     * (hojas + hijos de cada grupo), para desambiguar coincidencias por
     * primer segmento entre módulos DISTINTOS que comparten prefijo — ej.
     * "Unidades por liberar" (unit/departure-checks) es un módulo top-level
     * separado de "Unidades" (unit/list, unit/intelligence-center), pero
     * los tres comparten basename "unit". */
    private allNavigableItems(): any[] {
        const result: any[] = [];
        for (const item of (this.permissions || [])) {
            if (item?.children?.length > 0) {
                result.push(...item.children);
            } else {
                result.push(item);
            }
        }
        return result;
    }

    /**
     * Resalta un ítem solo cuando es el mejor candidato entre TODOS los
     * ítems navegables, no simplemente "cualquiera con el mismo primer
     * segmento". Antes, dos rutas que comparten basename (ej.
     * "unit/intelligence-center", "unit/list" y "unit/departure-checks",
     * las tres con basename "unit") quedaban resaltadas a la vez al entrar
     * a cualquiera de ellas, porque cada una se evaluaba de forma aislada
     * sin saber que otra ya tenía una coincidencia más específica. Ahora:
     * 1) gana el ítem cuya ruta es exacta o prefijo real de la URL actual
     * (el más largo, si hay varios); 2) si ninguno matchea por ruta, se usa
     * el basename SOLO si es ambiguo para un único ítem — si empatan dos o
     * más (páginas satélite de un módulo que no son la ruta propia de
     * ningún ítem, ej. "unit/add"), no se resalta ninguno en vez de
     * resaltar varios a la vez.
     */
    private isBestNavMatch(target: any, candidates: any[]): boolean {
        const currentPath = this.router.url.split('?')[0];
        const withPaths = candidates
            .map((c) => ({ item: c, path: this.routePath(c) }))
            .filter((c) => c.path !== '/');

        const pathMatches = withPaths.filter((c) => currentPath === c.path || currentPath.startsWith(c.path + '/'));
        if (pathMatches.length > 0) {
            const best = pathMatches.reduce((a, b) => (b.path.length > a.path.length ? b : a));
            return best.item === target;
        }

        const currentBase = this.baseSegment(currentPath);
        const baseMatches = currentBase ? withPaths.filter((c) => currentBase === this.baseSegment(c.path)) : [];
        return baseMatches.length === 1 && baseMatches[0].item === target;
    }

    isItemActive(item: any): boolean {
        if (item?.children?.length > 0) {
            const allItems = this.allNavigableItems();
            return item.children.some((child: any) => this.isBestNavMatch(child, allItems));
        }
        return this.isBestNavMatch(item, this.allNavigableItems());
    }

    /**
     * `siblings` (los demás children del mismo padre) evita que dos hermanos
     * con el mismo primer segmento (ej. "/provider/list" y "/provider/monthly",
     * ambos bajo "provider") se marquen activos a la vez: el fallback por
     * segmento base solo aplica si ningún hermano ya matcheó exacto.
     */
    isSubItemActive(subItem: any, siblings: any[] = []): boolean {
        const currentPath = this.router.url.split('?')[0];
        const path = this.routePath(subItem);
        if (currentPath === path) return true;

        const hasExactSiblingMatch = siblings.some((s: any) => s !== subItem && this.routePath(s) === currentPath);
        if (hasExactSiblingMatch) return false;

        if (this.baseSegment(currentPath) === '' || this.baseSegment(currentPath) !== this.baseSegment(path)) {
            return false;
        }

        // Ambigüedad: currentPath comparte el primer segmento con más de un
        // hermano (ej. "/provider/update/5" vs "provider/list" Y
        // "provider/monthly" -- ninguno matchea exacto, pero ambos matchean
        // por base). Las pantallas de alta/edición/detalle siempre
        // "pertenecen" visualmente al listado del módulo (MODULE_LIST_ROUTES),
        // nunca a una sección hermana como "Gestión mensual".
        const siblingsWithSameBase = siblings.filter((s: any) => this.baseSegment(this.routePath(s)) === this.baseSegment(path));
        if (siblingsWithSameBase.length > 1) {
            const preferredPath = this.MODULE_LIST_ROUTES[this.dataChildren?.['module']];
            return !!preferredPath && preferredPath === path;
        }

        return true;
    }

    /**
     * Resuelve el nombre del archivo del icono dentro de assets/img/icons/.
     *
     * El backend almacena `icon` como clase de FontAwesome (ej: "fa fa-home"),
     * lo que rompe el <img src="...{{icon}}">. Aquí mapeamos por nombre de
     * módulo al archivo real existente en assets, con fallback a `elements.svg`.
     */
    getIcon(item: any): string {
        const raw: string = (item?.icon ?? '').toString().trim();

        // Si el backend ya manda un nombre limpio (sin "fa", sin slash),
        // lo respetamos: "truck", "user.svg", "servicios.png"…
        // "null"/"undefined" en texto (dato mal guardado en BD) no cuenta como archivo.
        const rawLower = raw.toLowerCase();
        const looksLikeFile = raw && rawLower !== 'null' && rawLower !== 'undefined'
            && !raw.includes(' ') && !raw.includes('/') && !raw.startsWith('fa');
        if (looksLikeFile) {
            return /\.(svg|png|jpe?g|gif|webp)$/i.test(raw) ? raw : `${raw}.svg`;
        }

        // Mapeo por nombre de módulo (lo que sí varía y es estable)
        const name: string = (item?.name ?? '').toString().toLowerCase();
        const map: { [k: string]: string } = {
            'dashboard': 'dashboard.svg',
            'kpis servicios': 'chart.svg',
            'calendario': 'calendar.svg',
            'servicio': 'servicios.png',
            'servicios unificados': 'serviciosUnificados.png',
            'clientes': 'Clientes.png',
            'conductores': 'user-sharing.svg',
            'auxiliares': 'auxiliares.png',
            'unidades': 'truck.svg',
            'remolques': 'remolque.svg',
            'cuentas de usuarios': 'user-account.svg',
            'carta porte': 'carta-porte.svg',
            'mantenimiento de unidades': 'wrench.svg',
            'gastos': 'money-safe.svg',
            'control de gastos': 'money-safe.svg',
            'facturación': 'invoice.svg',
            'facturas de servicios': 'invoice.svg',
            'facturas de almacen': 'invoice.svg',
            'facturas de almacén': 'invoice.svg',
            'reportes': 'news.svg',
            'colaboradores': 'user-check-01.svg',
            'listado de colaboradores': 'sidebar-listado-colaboradores.svg',
            'recibos electrónicos': 'sidebar-recibos-electronicos.svg',
            'parámetros de nómina': 'sidebar-configuration-02.svg',
            'administrador de periodos': 'inc-calendar-03.svg',
            'pagos a proveedores': 'crowdfunding.svg',
            'gestión mensual': 'chart-line-data-02.svg',
            'proveedores': 'license-third-party.svg',
        };
        return map[name] || 'elements.svg';
    }

    /**
     * Renombra para mostrar etiquetas del menú distintas al nombre del backend.
     * "Proveedores" se muestra como "Listado de proveedores" (submenú de
     * "Pagos a proveedores") sin renombrar el módulo real en BD, para no
     * romper los canDo('Proveedores', ...) ya usados en el frontend.
     */
    getDisplayName(item: any): string {
        const name: string = (item?.name ?? '').toString();
        if (name.toLowerCase() === 'servicio') return 'Servicios';
        if (name.toLowerCase() === 'proveedores') return 'Listado de proveedores';
        return name;
    }

    // Permission helpers
    canDo(moduleKey: string, actionName: string): boolean {
        return this.permissionService.canDo(moduleKey, actionName);
    }
}